import { LANDING_SCREEN } from "./landingAssets";
import { TRIAL_DAYS } from "../../../constants/trial";
import {
  CRM_LANDING_PAGE_DESCRIPTION,
  CRM_LANDING_PAGE_KEYWORDS,
  CRM_LANDING_PAGE_TITLE,
  JOURNAL_LANDING_PAGE_DESCRIPTION,
  JOURNAL_LANDING_PAGE_KEYWORDS,
  JOURNAL_LANDING_PAGE_TITLE,
  TRAINER_LANDING_PAGE_DESCRIPTION,
  TRAINER_LANDING_PAGE_KEYWORDS,
  TRAINER_LANDING_PAGE_TITLE,
  buildCrmLandingJsonLd,
  buildJournalLandingJsonLd,
  buildTrainerLandingJsonLd,
} from "./landingSeo";

/**
 * Три рекламных лендинга под Direct (SEARCH | Core 20k):
 * journal · trainer · crm
 * Главная `/` — отдельный брендовый лендинг, её не трогаем.
 */

export const JOURNAL_LANDING = {
  metrika: "journal",
  hideFindClub: true,
  featuredPlanCode: null,
  heroFrame: "phone",
  seo: {
    title: JOURNAL_LANDING_PAGE_TITLE,
    description: JOURNAL_LANDING_PAGE_DESCRIPTION,
    keywords: JOURNAL_LANDING_PAGE_KEYWORDS,
    jsonLdBuilder: buildJournalLandingJsonLd,
  },
  heroScreen: {
    src: LANDING_SCREEN.manageJournal,
    alt: "Электронный журнал посещаемости в Бай.Спорт",
  },
  storySectionIds: ["journal"],
  trialNote: `${TRIAL_DAYS} дней бесплатно · карта не нужна · отметка с телефона`,
  metrics: [
    { value: "1 клик", label: "отметка в журнале" },
    { value: "0 ₽", label: `первые ${TRIAL_DAYS} дней` },
    { value: "от 790 ₽", label: "потом за клуб" },
  ],
  hero: {
    eyebrow: `Электронный журнал · ${TRIAL_DAYS} дней бесплатно`,
    h1Line1: "Электронный журнал",
    h1Line2: "посещаемости секции",
    lead:
      "Открыли занятие, отметили кто пришёл — занятие списалось с абонемента. Родители видят посещения в кабинете. Всё с телефона, без тетради и Excel.",
    primaryCta: `${TRIAL_DAYS} дней бесплатно`,
    uspTitle: "Зачем уходить от тетради",
    uspLead: "Тот же жест на тренировке — но без ручной работы вечером и вопросов в чате.",
    featuresTitle: "Что ещё есть рядом",
    featuresLead: "Журнал связан с расписанием, абонементами и кабинетом родителей.",
    stepsTitle: "Запуск за один вечер",
    stepsLead: "Не нужно переносить весь архив. Достаточно тех, кто ходит сейчас.",
    pricingTitle: "Тарифы",
    pricingLead: `Журнал есть на каждом тарифе. Первые ${TRIAL_DAYS} дней — полный доступ, без карты.`,
    ctaTitle: "Попробуйте журнал на своей группе",
    ctaLead: `${TRIAL_DAYS} дней бесплатно. Потом — от 790 ₽ в месяц. Карта не нужна.`,
  },
  uspItems: [
    {
      badge: "На занятии",
      icon: "01",
      title: "Отметка с телефона",
      text: "Список группы уже в журнале. Поставили галочку — и пошли дальше вести тренировку.",
      contrast: "Не вечером дома с тетрадкой.",
    },
    {
      badge: "Сразу",
      icon: "02",
      title: "Абонемент обновляется сам",
      text: "Занятие списывается в момент отметки. Остаток виден вам и родителю.",
      contrast: "Не в Excel в конце месяца.",
    },
    {
      badge: "После",
      icon: "03",
      title: "Родители видят сами",
      text: "Расписание, кто был, сколько занятий осталось — в кабинете секции.",
      contrast: "Не «ребёнок сегодня был?» в WhatsApp.",
    },
  ],
  featureHighlights: [
    {
      icon: "01",
      title: "Расписание",
      text: "Занятие открывается из календаря. Виден состав группы.",
    },
    {
      icon: "02",
      title: "Абонементы",
      text: "По занятиям или по сроку. После отметки остаток меняется сам.",
    },
    {
      icon: "03",
      title: "Кабинет родителей",
      text: "Посещения на телефоне. Приложение из магазина ставить не нужно.",
    },
  ],
  steps: [
    {
      num: "1",
      title: "Создайте секцию",
      text: `Название и группа. ${TRIAL_DAYS} дней полного доступа — без карты.`,
    },
    {
      num: "2",
      title: "Добавьте учеников",
      text: "Вручную или по ссылке родителям. Сразу можно завести абонементы.",
    },
    {
      num: "3",
      title: "Отметьте ближайшую тренировку",
      text: "Журнал с телефона. Родители увидят посещения сами.",
    },
  ],
  faq: [
    {
      q: "Нужен ли компьютер в зале?",
      a: "Нет. Журнал открывается в браузере телефона — можно сохранить на экран как приложение.",
    },
    {
      q: "Что происходит с абонементом после отметки?",
      a: "Занятие списывается сразу. Остаток виден вам и родителю в кабинете.",
    },
    {
      q: "Родители сами видят посещения?",
      a: "Да: расписание, кто был на занятии и сколько занятий осталось.",
    },
    {
      q: "Сколько это стоит?",
      a: "От 790 ₽ в месяц за клуб. Первые 7 дней бесплатно, карта не нужна.",
    },
  ],
};

export const TRAINER_LANDING = {
  metrika: "trainer",
  hideFindClub: true,
  featuredPlanCode: "basic",
  heroFrame: "phone",
  seo: {
    title: TRAINER_LANDING_PAGE_TITLE,
    description: TRAINER_LANDING_PAGE_DESCRIPTION,
    keywords: TRAINER_LANDING_PAGE_KEYWORDS,
    jsonLdBuilder: buildTrainerLandingJsonLd,
  },
  heroScreen: {
    src: LANDING_SCREEN.staffOverview,
    alt: "Кабинет тренера: сводка и ближайшие занятия",
  },
  storySectionIds: ["trainer"],
  trialNote: `${TRIAL_DAYS} дней бесплатно · без карты · стартовый от 790 ₽`,
  metrics: [
    { value: "1 тренер", label: "своя группа" },
    { value: "790 ₽", label: "стартовый тариф" },
    { value: `${TRIAL_DAYS} дней`, label: "бесплатно" },
  ],
  hero: {
    eyebrow: `Программа для тренера · ${TRIAL_DAYS} дней бесплатно`,
    h1Line1: "Журнал, абонементы и кабинет",
    h1Line2: "одному тренеру",
    lead:
      "Одна секция, телефон в зале. Расписание, отметки, абонементы и ответы родителям — без тетради, Excel и команды внедренцев.",
    primaryCta: `${TRIAL_DAYS} дней бесплатно`,
    uspTitle: "Вместо тетради, таблицы и чата",
    uspLead: "Три привычные задачи тренера — в одном кабинете на телефоне.",
    featuresTitle: "Что входит в кабинет",
    featuresLead: "Только то, чем вы реально пользуетесь каждый день.",
    stepsTitle: "Как начать",
    stepsLead: "Архив переносить не обязательно. Начните с текущей группы.",
    pricingTitle: "Тарифы",
    pricingLead: `Если работаете один — обычно хватает стартового за 790 ₽. Команда появится позже — тариф меняется, данные остаются. ${TRIAL_DAYS} дней полного доступа бесплатно.`,
    ctaTitle: "Попробуйте со своей группой",
    ctaLead: `${TRIAL_DAYS} дней бесплатно. Потом — от 790 ₽ в месяц. Карта не нужна.`,
  },
  uspItems: [
    {
      badge: "В зале",
      icon: "01",
      title: "Журнал на телефоне",
      text: "Отметили, кто пришёл. Абонемент обновился. Компьютер в зале не нужен.",
      contrast: "Не тетрадь после тренировки.",
    },
    {
      badge: "По деньгам",
      icon: "02",
      title: "Абонементы под контролем",
      text: "Видно, у кого заканчивается пакет. По занятиям или по сроку.",
      contrast: "Не таблица раз в месяц.",
    },
    {
      badge: "С родителями",
      icon: "03",
      title: "Кабинет вместо чата",
      text: "Расписание, посещения, остаток занятий — родители смотрят сами.",
      contrast: "Не сто сообщений «сегодня будет?»",
    },
  ],
  featureHighlights: [
    {
      icon: "01",
      title: "Группа и расписание",
      text: "Карточки учеников, повторяющиеся тренировки, опрос «приду».",
    },
    {
      icon: "02",
      title: "Журнал и абонементы",
      text: "Отметка на занятии и списание пакета в одном шаге.",
    },
    {
      icon: "03",
      title: "Кабинет родителей",
      text: "На телефоне, без App Store. Можно оформить под вашу секцию.",
    },
  ],
  steps: [
    {
      num: "1",
      title: "Создайте секцию",
      text: "Название и направление. Карту привязывать не нужно.",
    },
    {
      num: "2",
      title: "Добавьте тех, кто ходит",
      text: "Сами или по ссылке родителям. Сразу заведите абонементы.",
    },
    {
      num: "3",
      title: "Отметьте тренировку",
      text: "С телефона. После этого родители видят посещения сами.",
    },
  ],
  faq: [
    {
      q: "Это для одного тренера или для клуба?",
      a: "Эта страница — для тренера, который ведёт секцию сам. Клубу с командой лучше страница «CRM для спортивного клуба».",
    },
    {
      q: "С какого тарифа начинать?",
      a: "Со стартового — 790 ₽ в месяц, до 25 учеников. Если людей станет больше, тариф меняется без потери данных.",
    },
    {
      q: "Нужен ли компьютер в зале?",
      a: "Нет. Журнал и расписание открываются на телефоне.",
    },
    {
      q: "Сколько стоит первый месяц?",
      a: "Первые 7 дней бесплатно, карта не нужна. Потом вы выбираете тариф.",
    },
  ],
};

export const CLUB_LANDING = {
  metrika: "crm",
  hideFindClub: true,
  featuredPlanCode: "club",
  heroFrame: "desktop",
  seo: {
    title: CRM_LANDING_PAGE_TITLE,
    description: CRM_LANDING_PAGE_DESCRIPTION,
    keywords: CRM_LANDING_PAGE_KEYWORDS,
    jsonLdBuilder: buildCrmLandingJsonLd,
  },
  heroScreen: {
    src: LANDING_SCREEN.manageOverview,
    alt: "CRM спортивного клуба: сводка дня",
  },
  storySectionIds: ["club"],
  trialNote: `${TRIAL_DAYS} дней «Максимального» бесплатно · карта не нужна`,
  metrics: [
    { value: "CRM", label: "вместо Excel" },
    { value: "1 490 ₽", label: "для команды" },
    { value: `${TRIAL_DAYS} дней`, label: "бесплатно" },
  ],
  hero: {
    eyebrow: `CRM для спорта · ${TRIAL_DAYS} дней бесплатно`,
    h1Line1: "CRM для спортивного клуба",
    h1Line2: "не набор таблиц",
    lead:
      "Тренеры ведут журнал своей группы. Администратор видит абонементы и должников. Родители — в кабинете клуба под вашим брендом. Без Excel и хаоса в WhatsApp.",
    primaryCta: `${TRIAL_DAYS} дней бесплатно`,
    uspTitle: "Чего нет у таблиц и «просто CRM»",
    uspLead: "Обычные системы дают учёт. Здесь — учёт плюс кабинет, в который родители реально заходят.",
    featuresTitle: "Что входит в CRM клуба",
    featuresLead: "Операционка секции в одном кабинете — от журнала до оплат.",
    stepsTitle: "Как запустить",
    stepsLead: "Можно начать с одной площадки и текущих групп.",
    pricingTitle: "Тарифы",
    pricingLead: `Клубу с командой обычно подходит расширенный — 1 490 ₽. Стартовый 790 ₽ — если работаете один. ${TRIAL_DAYS} дней полного доступа бесплатно.`,
    ctaTitle: "Запустите CRM для своего клуба",
    ctaLead: `${TRIAL_DAYS} дней бесплатно. Потом выберите тариф под размер команды. Карта не нужна.`,
  },
  uspItems: [
    {
      badge: "Команда",
      icon: "01",
      title: "Кабинеты тренеров",
      text: "У каждого свой журнал и группа. Владелец видит клуб целиком.",
      contrast: "Не общая таблица на всех.",
    },
    {
      badge: "Деньги",
      icon: "02",
      title: "Абонементы и должники",
      text: "Кто без пакета, кто должен — видно в тот же день, не в конце месяца.",
      contrast: "Не Excel в воскресенье вечером.",
    },
    {
      badge: "Бренд",
      icon: "03",
      title: "Кабинет под ваш клуб",
      text: "Логотип, цвета, приложение на телефоне родителя. Не чужой сервис.",
      contrast: "Не ещё один безымянный портал.",
    },
  ],
  featureHighlights: [
    {
      icon: "01",
      title: "Журнал и расписание",
      text: "Занятия по группам, отметки, списание с абонемента.",
    },
    {
      icon: "02",
      title: "Клиенты и оплаты",
      text: "База учеников, типы абонементов, контроль должников.",
    },
    {
      icon: "03",
      title: "Кабинет родителей",
      text: "Расписание, посещения, чат и лента клуба — на телефоне.",
    },
    {
      icon: "04",
      title: "Несколько тренеров",
      text: "Отдельные кабинеты и общая картина для владельца.",
    },
  ],
  steps: [
    {
      num: "1",
      title: "Создайте клуб",
      text: `Название и логотип. ${TRIAL_DAYS} дней «Максимального» — без карты.`,
    },
    {
      num: "2",
      title: "Добавьте тренеров и группы",
      text: "Пригласите команду. У каждого свой журнал.",
    },
    {
      num: "3",
      title: "Отправьте ссылку родителям",
      text: "Они заходят в кабинет клуба. Вы работаете как обычно.",
    },
  ],
  faq: [
    {
      q: "Чем это лучше Excel?",
      a: "Журнал, абонементы и кабинет родителей связаны. Отметили занятие — пакет обновился, родитель увидел посещение. В таблице это три разных файла.",
    },
    {
      q: "Это для одного тренера или для клуба?",
      a: "Эта страница — для клуба с командой. Если секцию ведёте сами, откройте «для тренера» — там другой сценарий и обычно стартовый тариф.",
    },
    {
      q: "Родители видят Бай.Спорт или наш клуб?",
      a: "На расширенном и максимальном кабинет оформляется под клуб: логотип и цвета.",
    },
    {
      q: "Сколько это стоит?",
      a: "Первые 7 дней бесплатно. Клубу с командой обычно 1 490 ₽ в месяц. Один тренер — от 790 ₽.",
    },
  ],
};

const AD_LANDINGS = {
  journal: JOURNAL_LANDING,
  trainer: TRAINER_LANDING,
  crm: CLUB_LANDING,
};

export function getAdLanding(variant) {
  return AD_LANDINGS[variant] || null;
}
